import { useState, useRef, useEffect, FormEvent } from 'react';
import { api } from '../api';

interface Message {
  id: string;
  role: 'user' | 'bot';
  content: string;
  timestamp: Date;
}

interface ChatPreviewProps {
  botName?: string;
  greeting?: string;
  className?: string;
}

export default function ChatPreview({
  botName = 'Zeus Bot',
  greeting,
  className = '',
}: ChatPreviewProps) {
  const [messages, setMessages] = useState<Message[]>(() => {
    const initial: Message[] = [];
    if (greeting) {
      initial.push({
        id: 'greeting',
        role: 'bot',
        content: greeting,
        timestamp: new Date(),
      });
    }
    return initial;
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  // Update greeting if it changes
  useEffect(() => {
    if (greeting) {
      setMessages((prev) => {
        const withoutGreeting = prev.filter((m) => m.id !== 'greeting');
        return [
          { id: 'greeting', role: 'bot', content: greeting, timestamp: new Date() },
          ...withoutGreeting,
        ];
      });
    }
  }, [greeting]);

  const handleSend = async (e: FormEvent) => {
    e.preventDefault();
    if (!input.trim() || sending) return;

    const userMsg: Message = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: input.trim(),
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setSending(true);

    try {
      const res = await api.post<{ message: string }>('/chat/test', {
        message: userMsg.content,
      });

      const botMsg: Message = {
        id: `bot-${Date.now()}`,
        role: 'bot',
        content: res.ok && res.data?.message
          ? res.data.message
          : `Hello! I'm ${botName}. I received your message but the test endpoint isn't connected yet. This is a preview of how conversations will look.`,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, botMsg]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `bot-${Date.now()}`,
          role: 'bot',
          content: `I'm ${botName} and I'm ready to chat! (Test mode - API not connected)`,
          timestamp: new Date(),
        },
      ]);
    }

    setSending(false);
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

  return (
    <div className={`flex flex-col bg-surface-secondary border border-border rounded-card overflow-hidden ${className}`} style={{ height: '400px' }}>
      {/* Chat header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border bg-surface-tertiary">
        <div className="w-7 h-7 rounded-full bg-accent/10 border border-accent/20 flex items-center justify-center">
          <span className="text-accent text-xs font-bold">{botName[0]?.toUpperCase()}</span>
        </div>
        <div>
          <p className="text-sm font-medium text-text-primary">{botName}</p>
          <p className="text-[10px] text-accent flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-accent inline-block" />
            Online
          </p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3">
        {messages.length === 0 && (
          <div className="flex items-center justify-center h-full text-text-muted text-sm">
            Send a message to test your bot
          </div>
        )}
        {messages.map((msg) => (
          <div
            key={msg.id}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-lg px-3 py-2 ${
                msg.role === 'user'
                  ? 'bg-accent/10 border border-accent/20 text-text-primary'
                  : 'bg-surface-tertiary border border-border text-text-primary'
              }`}
            >
              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
              <p className="text-[10px] text-text-muted mt-1">{formatTime(msg.timestamp)}</p>
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="bg-surface-tertiary border border-border rounded-lg px-4 py-3">
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-text-muted animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Input */}
      <form onSubmit={handleSend} className="flex gap-2 p-3 border-t border-border">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          className="input-base flex-1 h-9"
          placeholder="Type a message..."
          disabled={sending}
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          className="btn-primary h-9 px-4 text-sm flex items-center justify-center"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
        </button>
      </form>
    </div>
  );
}
